import styled from 'styled-components';
import Navbar from './Navbar';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { motion } from 'framer-motion';

const Bar = styled.header`
  position: sticky; top: 0; z-index: 50;
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 20px; background: #fff; border-bottom: 1px solid #e5e7eb;
`;
const Brand = styled.div`display:flex; align-items:center; gap:10px; font-weight:800; color:#0f172a;`;
const Lang = styled.div`display:flex; gap:8px;`;
const LangBtn = styled.button`
  padding: 6px 10px; border-radius: 6px; border: 1px solid #e5e7eb; background:#fff; cursor:pointer;
  &.active{ background:#0ea5e9; color:#fff; border-color:#0ea5e9; }
`;

const Burger = styled.button`
  display: none;
  @media (max-width: 768px){
    display: inline-flex; align-items:center; justify-content:center;
    width: 40px; height: 40px; border-radius: 8px; border:1px solid #e5e7eb; background:#fff; cursor:pointer;
  }
`;

const DesktopNavWrap = styled.div`
  display:flex; align-items:center; gap:16px;
  @media (max-width: 768px){ display:none; }
`;

const Drawer = styled.aside`
  position: fixed; inset: 0 0 0 0; z-index: 60; pointer-events: ${({open}) => open ? 'auto' : 'none'};
`;
const Backdrop = styled.div`
  position:absolute; inset:0; background: rgba(0,0,0,.35);
  opacity:${({open})=>open?1:0}; transition: opacity .2s ease;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
`;
const Panel = styled.nav`
  position:absolute; top:0; left:0; height:100%; width:min(82vw, 320px);
  background:#fff; border-right:1px solid #e5e7eb; padding:16px; transform: translateX(${({open})=>open? '0' : '-100%'});
  transition: transform .25s ease;
  display:flex; flex-direction:column; gap:16px;
`;
const DrawerSection = styled.div`display:flex; flex-direction:column; gap:12px;`;

const MDrawerList = styled(motion.ul)`
  list-style:none; margin:0; padding:8px 0; display:flex; flex-direction:column;
`;
const MDrawerItem = styled(motion.li)`
  border-bottom:1px solid #e5e7eb;
  &:last-child{ border-bottom:none; }
  a{ display:block; padding:12px 4px; color:#0f172a; text-decoration:none; font-weight:600; }
`;

export default function Header() {
  const { i18n, t } = useTranslation();
  const { lng: urlLng } = useParams();
  const current = (urlLng === 'sr' || urlLng === 'en') ? urlLng : (i18n.language?.startsWith('sr') ? 'sr' : 'en');
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const setLang = (lng) => () => {
    i18n.changeLanguage(lng);
    // replace first path segment with new lng
    const parts = location.pathname.split('/');
    if (parts.length > 1) {
      parts[1] = lng;
      navigate(parts.join('/'), { replace: true });
    } else {
      navigate(`/${lng}`, { replace: true });
    }
    setOpen(false);
  };

  return (
    <Bar>
      <Brand>
  <img src={process.env.PUBLIC_URL + '/assets/logo.svg'} alt="Moving Co. logo" width={120} height={30} />
      </Brand>
      <DesktopNavWrap>
        <Navbar />
        <Lang>
          <LangBtn onClick={setLang('sr')} className={current==='sr' ? 'active' : ''}>SRB</LangBtn>
          <LangBtn onClick={setLang('en')} className={current==='en' ? 'active' : ''}>ENG</LangBtn>
        </Lang>
      </DesktopNavWrap>
      <Burger aria-label="Open menu" onClick={()=>setOpen(true)}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
      </Burger>
      <Drawer open={open} aria-hidden={!open}>
        <Backdrop open={open} onClick={()=>setOpen(false)} />
        <Panel open={open} aria-label="Mobile Navigation">
          <button onClick={()=>setOpen(false)} aria-label="Close menu" style={{ alignSelf:'flex-end', background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, width:36, height:36 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <DrawerSection>
            <MDrawerList
              initial="hidden"
              animate={open ? 'visible' : 'hidden'}
              variants={{ hidden:{ opacity:0 }, visible:{ opacity:1, transition:{ staggerChildren:0.06, delayChildren:0.05 } } }}
            >
              <MDrawerItem variants={{ hidden:{ opacity:0, y:6 }, visible:{ opacity:1, y:0 } }}>
                <NavLink to={`/${current}`} onClick={()=>setOpen(false)}>{t('nav.home')}</NavLink>
              </MDrawerItem>
              <MDrawerItem variants={{ hidden:{ opacity:0, y:6 }, visible:{ opacity:1, y:0 } }}>
                <NavLink to={`/${current}/quote`} onClick={()=>setOpen(false)}>{t('nav.quote')}</NavLink>
              </MDrawerItem>
              <MDrawerItem variants={{ hidden:{ opacity:0, y:6 }, visible:{ opacity:1, y:0 } }}>
                <NavLink to={`/${current}/blog`} onClick={()=>setOpen(false)}>{t('nav.blog')}</NavLink>
              </MDrawerItem>
              <MDrawerItem variants={{ hidden:{ opacity:0, y:6 }, visible:{ opacity:1, y:0 } }}>
                <NavLink to={`/${current}/contact`} onClick={()=>setOpen(false)}>{t('nav.contact')}</NavLink>
              </MDrawerItem>
            </MDrawerList>
          </DrawerSection>
          <DrawerSection>
            <strong>Language</strong>
            <div style={{ display:'flex', gap:8 }}>
              <LangBtn onClick={setLang('sr')} className={current==='sr' ? 'active' : ''}>SRB</LangBtn>
              <LangBtn onClick={setLang('en')} className={current==='en' ? 'active' : ''}>ENG</LangBtn>
            </div>
          </DrawerSection>
        </Panel>
      </Drawer>
    </Bar>
  );
}
